// src/pages/StyleGuidePage.jsx
// TEMPORARY: Comprehensive BRIO Design System Style Guide
// Purpose: Visual inventory of all styles, components, and patterns for design review.
// Delete this file when design decisions are finalized.

import React, { useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioModalDivider,
  BrioFrame,
  BrioFrameHeader,
  BrioTableWrapper,
  BrioListWrapper,
  BrioAnalyticsSurface,
  BrioPageHeader,
  BrioPageSubHeader,
  BrioSectionHeader,
  BrioByCode,
  BrioTable,
  BrioListItem,
  BrioTeamIcon,
  BrioUserIcon,
  BrioIconButton,
  BrioBadge,
  BrioColorIndicator,
  BrioProgress,
  BrioAlert,
  BrioHighlightBox,
  BrioActionGroup,
  BrioCard,
  BrioKpiBlock,
  BrioButton,
  BrioInfoBanner,
  BrioEmptyState,
  BrioProgressBar,
  BrioInput,
  BrioSelect,
  BrioGrid,
  BrioSection,
  getTablePropsFromCode,
  isListCode,
  BrioDatePicker,
  BrioMonthPicker,
  BrioDateRangePicker,
} from '../components/BrioComponents';
import {
  Edit2, Trash2, Plus, Check, X, AlertTriangle, AlertCircle,
  ChevronDown, ChevronRight, ChevronUp, Copy, Download, Upload,
  RefreshCw, Loader2, Clock, Users, Briefcase, MapPin, Mail,
  Paperclip, Search, Settings, Eye, EyeOff, Star, Heart, Calendar,
  FileText, Filter, MoreHorizontal, ExternalLink,
} from 'lucide-react';

// ─── Section wrapper with anchor ID ─────────────────────────────────────────
const Section = ({ id, number, title, description, children }) => (
  <div id={id} style={{ marginBottom: 56 }}>
    <div style={{
      borderBottom: '2px solid var(--brio-gold)',
      paddingBottom: 8,
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--brio-gold)',
          background: 'var(--brio-gold-light)',
          padding: '2px 8px',
          letterSpacing: '0.05em',
        }}>{number}</span>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--brio-black)', margin: 0 }}>
          {title}
        </h2>
      </div>
      {description && (
        <p style={{ fontSize: 11, color: 'var(--brio-grey)', margin: '6px 0 0', fontStyle: 'italic' }}>
          {description}
        </p>
      )}
    </div>
    {children}
  </div>
);

// ─── Specimen label ─────────────────────────────────────────────────────────
const Label = ({ children, code }) => (
  <div style={{
    fontSize: 10,
    fontWeight: 500,
    color: 'var(--brio-grey)',
    marginBottom: 6,
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  }}>
    {code && (
      <span style={{
        fontSize: 9,
        fontWeight: 700,
        background: '#f0f0f0',
        padding: '1px 5px',
        fontFamily: 'monospace',
      }}>{code}</span>
    )}
    {children}
  </div>
);

// ─── Swatch for displaying a color ──────────────────────────────────────────
const Swatch = ({ name, value, cssVar, light }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
    <div style={{
      width: 40,
      height: 28,
      background: value,
      border: light ? '1px solid var(--brio-border)' : 'none',
      flexShrink: 0,
    }} />
    <div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--brio-black)' }}>{name}</div>
      <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
        {cssVar} · {value}
      </div>
    </div>
  </div>
);

// ─── Inline specimen box ────────────────────────────────────────────────────
const Specimen = ({ label, children, inline, code }) => (
  <div style={{ marginBottom: 16 }}>
    <Label code={code}>{label}</Label>
    <div style={inline ? { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' } : {}}>
      {children}
    </div>
  </div>
);

// ─── Suggestion badge ───────────────────────────────────────────────────────
const Suggestion = ({ type, children }) => {
  const colors = {
    keep: { bg: 'var(--brio-green-light)', border: 'var(--brio-green)', text: 'var(--brio-green)' },
    merge: { bg: 'var(--brio-yellow-light)', border: 'var(--brio-yellow)', text: '#8B7000' },
    replace: { bg: 'var(--brio-red-light)', border: 'var(--brio-red)', text: 'var(--brio-red)' },
    question: { bg: 'var(--brio-orange-light)', border: 'var(--brio-orange)', text: '#9A4F35' },
  };
  const c = colors[type] || colors.question;
  return (
    <div style={{
      fontSize: 10,
      padding: '6px 10px',
      background: c.bg,
      borderLeft: `3px solid ${c.border}`,
      color: c.text,
      marginTop: 8,
      lineHeight: 1.5,
    }}>
      <strong style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {type === 'keep' ? '✓ KEEP' : type === 'merge' ? '⟷ MERGE' : type === 'replace' ? '✗ REPLACE' : '? QUESTION'}
      </strong>
      {' — '}{children}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function StyleGuidePage() {
  const [showModal, setShowModal] = useState(false);
  const [showModalLg, setShowModalLg] = useState(false);
  const [showModalDanger, setShowModalDanger] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [inputValue, setInputValue] = useState('');

  // Table of contents data
  const toc = [
    { id: 'colors', num: '01', title: 'Color Palette' },
    { id: 'typography', num: '02', title: 'Typography' },
    { id: 'spacing', num: '03', title: 'Spacing & Layout' },
    { id: 'buttons', num: '04', title: 'Buttons & Actions' },
    { id: 'forms', num: '05', title: 'Form Elements' },
    { id: 'tables', num: '06', title: 'Tables (Presentation Codes)' },
    { id: 'lists', num: '07', title: 'List Items' },
    { id: 'badges', num: '08', title: 'Badges & Status' },
    { id: 'progress', num: '09', title: 'Progress Bars' },
    { id: 'icons-avatars', num: '10', title: 'Icons & Avatars' },
    { id: 'alerts', num: '11', title: 'Alerts & Highlights' },
    { id: 'modals', num: '12', title: 'Modals & Dialogs' },
    { id: 'headers', num: '13', title: 'Page Headers & Navigation' },
    { id: 'surfaces', num: '14', title: 'Surfaces & Cards' },
    { id: 'project-colors', num: '15', title: 'Project Type Colors' },
    { id: 'role-colors', num: '16', title: 'Role Seniority Colors' },
    { id: 'icons-library', num: '17', title: 'Icon Library (lucide-react)' },
    { id: 'suggestions', num: '18', title: 'Design Suggestions' },
  ];

  return (
    <div style={{ fontFamily: 'var(--brio-font-family)' }}>
      {/* ── Hero Header ──────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--brio-black)',
        color: 'var(--brio-white)',
        padding: '40px 24px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--brio-gold)', marginBottom: 8 }}>
          BRIO DESIGN SYSTEM
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 200, margin: '0 0 8px' }}>Style Guide & Component Inventory</h1>
        <p style={{ fontSize: 12, color: 'var(--brio-grey)', margin: 0, fontStyle: 'italic' }}>
          Temporary page — review, annotate, and decide which styles to keep, merge, or replace
        </p>
      </div>

      {/* ── Table of Contents ────────────────────────────────────────── */}
      <div style={{
        background: 'var(--brio-grey-light)',
        borderBottom: '1px solid var(--brio-border)',
        padding: '16px 24px',
      }}>
        <BrioFrame>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--brio-grey)', marginBottom: 10 }}>
            TABLE OF CONTENTS
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
            {toc.map(t => (
              <a
                key={t.id}
                href={`#${t.id}`}
                style={{
                  fontSize: 11,
                  color: 'var(--brio-black)',
                  textDecoration: 'none',
                  padding: '3px 0',
                }}
              >
                <span style={{ color: 'var(--brio-gold)', fontWeight: 700, marginRight: 4 }}>{t.num}</span>
                {t.title}
              </a>
            ))}
          </div>
        </BrioFrame>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <div style={{ padding: '32px 24px' }}>
        <BrioFrame>

          {/* ═══════ 01 — COLOR PALETTE ═══════ */}
          <Section id="colors" number="01" title="Color Palette" description="All design tokens defined as CSS variables (--brio-*). These are the only approved colors.">

            <Specimen label="Primary Brand">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <Swatch name="Gold (Primary)" value="#9F902A" cssVar="--brio-gold" />
                <Swatch name="Gold Hover" value="#7F7321" cssVar="--brio-gold-hover" />
                <Swatch name="Gold Light" value="rgba(159,144,42,0.1)" cssVar="--brio-gold-light" light />
              </div>
            </Specimen>

            <Specimen label="Neutrals">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <Swatch name="Black" value="#111111" cssVar="--brio-black" />
                <Swatch name="Grey" value="#666666" cssVar="--brio-grey" />
                <Swatch name="Grey Light" value="#f5f5f5" cssVar="--brio-grey-light" light />
                <Swatch name="White" value="#ffffff" cssVar="--brio-white" light />
                <Swatch name="Border" value="rgba(0,0,0,0.1)" cssVar="--brio-border" light />
              </div>
            </Specimen>

            <Specimen label="Semantic Colors">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <Swatch name="Green (Success)" value="#84A48C" cssVar="--brio-green" />
                <Swatch name="Green Light" value="rgba(132,164,140,0.15)" cssVar="--brio-green-light" light />
                <Swatch name="Red (Danger)" value="#934948" cssVar="--brio-red" />
                <Swatch name="Red Light" value="rgba(147,73,72,0.1)" cssVar="--brio-red-light" light />
                <Swatch name="Orange (Warning)" value="#E07A5F" cssVar="--brio-orange" />
                <Swatch name="Orange Light" value="rgba(224,122,95,0.1)" cssVar="--brio-orange-light" light />
                <Swatch name="Yellow (Caution)" value="#D4A72C" cssVar="--brio-yellow" />
                <Swatch name="Yellow Light" value="rgba(212,167,44,0.1)" cssVar="--brio-yellow-light" light />
              </div>
            </Specimen>

            <Suggestion type="question">
              Some pages use hardcoded hex colors (#d97706, #16a34a, #3B82F6) instead of BRIO tokens. Should we add Blue (#3B82F6) to the official palette, or map it to an existing color?
            </Suggestion>
          </Section>

          {/* ═══════ 02 — TYPOGRAPHY ═══════ */}
          <Section id="typography" number="02" title="Typography" description="Font family: Raleway (Google Fonts). Seven defined sizes (9–24px), three weights, italic variant.">

            <Specimen label="Type Scale (CSS Tokens)">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { token: '--brio-font-size-xs', size: '9px', use: 'Smallest labels, meta' },
                  { token: '--brio-font-size-s', size: '10px', use: 'Badges, small text, hints' },
                  { token: '--brio-font-size-m', size: '11px', use: 'Form labels, dense tables, subtitles' },
                  { token: '--brio-font-size-base', size: '12px', use: 'Default body text, inputs' },
                  { token: '--brio-font-size-l', size: '13px', use: 'Table default, list item titles' },
                  { token: '--brio-font-size-xl', size: '16px', use: 'Modal title, frame title, analytics section title' },
                  { token: '--brio-font-size-2xl', size: '24px', use: 'Page header title, KPI value' },
                ].map(t => (
                  <div key={t.token} style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
                    <span style={{ fontSize: t.size, fontWeight: 400, minWidth: 200 }}>
                      The quick brown fox jumps over the lazy dog
                    </span>
                    <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)', minWidth: 160 }}>
                      {t.token} ({t.size})
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--brio-grey)', fontStyle: 'italic' }}>{t.use}</span>
                  </div>
                ))}
              </div>
            </Specimen>

            <Specimen label="Font Weights">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { weight: 200, name: 'Light', use: 'Page header subtitle' },
                  { weight: 400, name: 'Regular', use: 'Body text, table headers, tabs' },
                  { weight: 500, name: 'Medium', use: 'Labels, modal titles, active tabs, KPI values' },
                  { weight: 700, name: 'Bold', use: 'Page title, icon buttons, section titles' },
                ].map(w => (
                  <div key={w.weight} style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
                    <span style={{ fontSize: 13, fontWeight: w.weight, minWidth: 200 }}>
                      Architecture BRIO — {w.name}
                    </span>
                    <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
                      weight: {w.weight} — {w.use}
                    </span>
                  </div>
                ))}
              </div>
            </Specimen>

            <Specimen label="Italic Style">
              <span style={{ fontSize: 13, fontStyle: 'italic', fontWeight: 200, color: 'var(--brio-grey)' }}>
                Page subtitles and metadata use italic light (200) grey
              </span>
            </Specimen>

            <Suggestion type="merge">
              Resolved: Added --brio-font-size-xl (16px) and --brio-font-size-2xl (24px). 15px removed from type scale — all 15px references updated to 16px.
            </Suggestion>
          </Section>

          {/* ═══════ 03 — SPACING & LAYOUT ═══════ */}
          <Section id="spacing" number="03" title="Spacing & Layout" description="Five-step spacing scale plus max-width tokens for layout constraint.">

            <Specimen label="Spacing Scale">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { token: '--brio-space-xs', size: 8 },
                  { token: '--brio-space-s', size: 16 },
                  { token: '--brio-space-m', size: 24 },
                  { token: '--brio-space-l', size: 32 },
                  { token: '--brio-space-xl', size: 48 },
                ].map(s => (
                  <div key={s.token} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: s.size,
                      height: 16,
                      background: 'var(--brio-gold)',
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
                      {s.token} = {s.size}px
                    </span>
                  </div>
                ))}
              </div>
            </Specimen>

            <Specimen label="Layout Max-Widths">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { token: '--brio-maxw-content', width: 540, use: 'Content sections' },
                  { token: '--brio-maxw-table', width: 875, use: 'Tables, lists (BrioFrame default)' },
                  { token: '--brio-maxw-container', width: 970, use: 'Full-width container' },
                ].map(l => (
                  <div key={l.token}>
                    <div style={{
                      width: `min(${l.width}px, 100%)`,
                      height: 8,
                      background: 'var(--brio-gold-light)',
                      border: '1px solid var(--brio-gold)',
                      marginBottom: 2,
                    }} />
                    <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
                      {l.token} = {l.width}px — {l.use}
                    </span>
                  </div>
                ))}
              </div>
            </Specimen>

            <Specimen label="Modal Widths">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { name: 'sm', width: 384 },
                  { name: 'md (default)', width: 448 },
                  { name: 'lg', width: 640 },
                  { name: 'xl', width: 768 },
                ].map(m => (
                  <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: `min(${m.width}px, 100%)`,
                      height: 6,
                      background: 'var(--brio-grey)',
                    }} />
                    <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
                      {m.name} = {m.width}px
                    </span>
                  </div>
                ))}
              </div>
            </Specimen>
          </Section>

          {/* ═══════ 04 — BUTTONS & ACTIONS ═══════ */}
          <Section id="buttons" number="04" title="Buttons & Actions" description="Three button variants plus icon buttons in three sizes.">

            <Specimen label="Button Variants (.brio-btn)" inline>
              <button className="brio-btn brio-btn-primary">Primary Action</button>
              <button className="brio-btn brio-btn-secondary">Secondary</button>
              <button className="brio-btn brio-btn-danger">Danger / Delete</button>
              <button className="brio-btn brio-btn-primary" disabled>Disabled</button>
            </Specimen>

            <Specimen label="BrioButton Component" inline>
              <BrioButton variant="primary">BrioButton Primary</BrioButton>
            </Specimen>

            <Specimen label="Full Width Button">
              <button className="brio-btn brio-btn-primary brio-btn--full">Full Width Button</button>
            </Specimen>

            <Specimen label="Icon Buttons (BrioIconButton)" inline>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div>
                  <Label>Edit (small)</Label>
                  <BrioIconButton icon="edit" variant="edit" size="small" title="Edit" />
                </div>
                <div>
                  <Label>Edit (default)</Label>
                  <BrioIconButton icon="edit" variant="edit" size="default" title="Edit" />
                </div>
                <div>
                  <Label>Edit (large)</Label>
                  <BrioIconButton icon="edit" variant="edit" size="large" title="Edit" />
                </div>
                <div>
                  <Label>Delete (default)</Label>
                  <BrioIconButton icon="delete" variant="delete" size="default" title="Delete" />
                </div>
                <div>
                  <Label>Disabled</Label>
                  <BrioIconButton icon="edit" variant="edit" disabled title="Disabled" />
                </div>
              </div>
            </Specimen>

            <Specimen label="Action Group (BrioActionGroup)" inline>
              <BrioActionGroup>
                <BrioIconButton icon="edit" variant="edit" title="Edit" />
                <BrioIconButton icon="delete" variant="delete" title="Delete" />
              </BrioActionGroup>
            </Specimen>

            <Suggestion type="keep">
              Three button variants are clean and sufficient. The icon button hover colors (gold for edit, red for delete) are intuitive.
            </Suggestion>
          </Section>

          {/* ═══════ 05 — FORM ELEMENTS ═══════ */}
          <Section id="forms" number="05" title="Form Elements" description="Input, select, and field layout components used inside modals and forms.">

            <Specimen label="Text Input (.brio-modal-input / BrioInput)">
              <div style={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <BrioInput placeholder="Default input..." value={inputValue} onChange={e => setInputValue(e.target.value)} />
                <input className="brio-modal-input brio-modal-input--error" placeholder="Error state input..." readOnly />
              </div>
            </Specimen>

            <Specimen label="Select Dropdown (.brio-modal-select / BrioSelect)">
              <div style={{ maxWidth: 400 }}>
                <BrioSelect>
                  <option value="">Select an option...</option>
                  <option value="a">Option A</option>
                  <option value="b">Option B</option>
                </BrioSelect>
              </div>
            </Specimen>

            <Specimen label="Modal Field Layout (BrioModalField)">
              <div style={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <BrioModalField label="Project Name" hint="This will appear on invoices">
                  <BrioInput placeholder="Enter project name..." />
                </BrioModalField>
                <BrioModalField label="Client">
                  <BrioSelect>
                    <option value="">Choose client...</option>
                    <option value="1">Client Alpha</option>
                  </BrioSelect>
                </BrioModalField>
              </div>
            </Specimen>

            <Specimen label="Modal Divider (BrioModalDivider)">
              <div style={{ maxWidth: 400 }}>
                <BrioModalDivider />
                <div style={{ height: 12 }} />
                <BrioModalDivider title="With Section Title" />
              </div>
            </Specimen>

            <Specimen label="Inline Input Style (from page files)">
              <div style={{ maxWidth: 400 }}>
                <Label code="INPUT_STYLE">Pattern used in ManageProjectsPage, TeamMembersPage</Label>
                <input
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid var(--brio-border, #d1d5db)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    background: '#fff',
                    color: 'var(--brio-text, #1e293b)',
                    boxSizing: 'border-box',
                    fontFamily: 'var(--brio-font-family)',
                  }}
                  placeholder="Inline styled input..."
                  readOnly
                />
              </div>
            </Specimen>

            <Suggestion type="question">
              Two input styles exist: (1) .brio-modal-input (4px radius, 8px 12px padding) and (2) inline INPUT_STYLE (6px radius, 6px 10px padding). Should we consolidate to one?
            </Suggestion>
          </Section>

          {/* ═══════ 06 — TABLES ═══════ */}
          <Section id="tables" number="06" title="Tables (Presentation Codes)" description="All 18 presentation codes across 6 sections × 3 variants (default/compact/dense + list). Only table variants shown here.">

            {/* Show one example of each table variant */}
            {[
              { code: '1a', title: 'Code 1a — Default, Sticky Header', section: 'Profitability' },
              { code: '1b', title: 'Code 1b — Compact, Sticky Header', section: 'Profitability' },
              { code: '4a', title: 'Code 4a — Dense', section: 'Leave Summary' },
              { code: '5a', title: 'Code 5a — Dense + Striped', section: 'Salary Matrix' },
            ].map(t => (
              <Specimen key={t.code} label={t.title} code={t.code}>
                <BrioByCode code={t.code}>
                  <BrioTable {...getTablePropsFromCode(t.code)}>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Project</th>
                        <th className="text-right">Hours</th>
                        <th className="text-right">Amount</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'Priya Sharma', project: 'Villa Westbroek', hours: 38, amount: '€2,400' },
                        { name: 'Jan de Vries', project: 'School Eindhoven', hours: 32, amount: '€3,100' },
                        { name: 'Ananya Desai', project: 'Temple Restoration', hours: 24, amount: '€1,800' },
                        { name: 'Mark Bakker', project: 'Hotel Mumbai', hours: 40, amount: '€4,200' },
                      ].map((row, i) => (
                        <tr key={i} className="brio-row--hoverable">
                          <td>{row.name}</td>
                          <td>{row.project}</td>
                          <td className="text-right">{row.hours}</td>
                          <td className="text-right">{row.amount}</td>
                          <td className="col-actions">
                            <BrioActionGroup>
                              <BrioIconButton icon="edit" variant="edit" size="small" />
                              <BrioIconButton icon="delete" variant="delete" size="small" />
                            </BrioActionGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </BrioTable>
                </BrioByCode>
              </Specimen>
            ))}

            <Specimen label="Row States" code="hover/exceeded/warning">
              <BrioByCode code="1a">
                <BrioTable {...getTablePropsFromCode('1a')}>
                  <thead>
                    <tr><th>State</th><th>Class</th><th className="text-right">Value</th></tr>
                  </thead>
                  <tbody>
                    <tr className="brio-row--hoverable"><td>Normal (hoverable)</td><td>.brio-row--hoverable</td><td className="text-right">—</td></tr>
                    <tr className="brio-row--hovered"><td>Hovered (forced)</td><td>.brio-row--hovered</td><td className="text-right">—</td></tr>
                    <tr className="brio-row--exceeded"><td>Exceeded (over budget)</td><td>.brio-row--exceeded</td><td className="text-right">120%</td></tr>
                    <tr className="brio-row--warning"><td>Warning</td><td>.brio-row--warning</td><td className="text-right">95%</td></tr>
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </Specimen>

            <Suggestion type="keep">
              The presentation code system is elegant. Code-driven table styling keeps pages clean.
            </Suggestion>
          </Section>

          {/* ═══════ 07 — LIST ITEMS ═══════ */}
          <Section id="lists" number="07" title="List Items" description="BrioListItem with variant, tone, stripe, and hover options.">

            <Specimen label="Default List Items" code="BrioListItem">
              <BrioListWrapper>
                <BrioListItem variant="default" hoverable>
                  <div className="brio-list-item__content">
                    <div className="brio-list-item__title">Default List Item — Hoverable</div>
                    <div className="brio-list-item__subtitle">Subtitle text in italic grey</div>
                  </div>
                  <div className="brio-list-item__meta">
                    <div className="brio-list-item__meta-item">€2,400</div>
                    <div className="brio-list-item__meta-label">total fee</div>
                  </div>
                </BrioListItem>

                <BrioListItem variant="compact" hoverable>
                  <div className="brio-list-item__content">
                    <div className="brio-list-item__title">Compact Variant</div>
                    <div className="brio-list-item__subtitle">Less padding (8px)</div>
                  </div>
                </BrioListItem>

                <BrioListItem variant="dense">
                  <div className="brio-list-item__content">
                    <div className="brio-list-item__title">Dense Variant (6px padding, 10px font)</div>
                  </div>
                </BrioListItem>
              </BrioListWrapper>
            </Specimen>

            <Specimen label="Alert Variants with Tone Stripes" code="tone">
              <BrioListWrapper>
                {['gold', 'red', 'orange', 'yellow', 'green', 'grey'].map(tone => (
                  <BrioListItem key={tone} variant="alert" tone={tone}>
                    <div className="brio-list-item__content">
                      <div className="brio-list-item__title">Alert — tone: {tone}</div>
                      <div className="brio-list-item__subtitle">.brio-tone--{tone} sets --brio-stripe</div>
                    </div>
                  </BrioListItem>
                ))}
              </BrioListWrapper>
            </Specimen>

            <Specimen label="Custom Stripe Color" code="stripeColor">
              <BrioListWrapper>
                <BrioListItem variant="alert" stripeColor="#DB6B5D">
                  <div className="brio-list-item__content">
                    <div className="brio-list-item__title">Custom Project Color Stripe</div>
                    <div className="brio-list-item__subtitle">stripeColor="#DB6B5D" (Cultural project)</div>
                  </div>
                </BrioListItem>
              </BrioListWrapper>
            </Specimen>
          </Section>

          {/* ═══════ 08 — BADGES & STATUS ═══════ */}
          <Section id="badges" number="08" title="Badges & Status Indicators" description="BrioBadge for status labels, BrioColorIndicator for project color bars.">

            <Specimen label="Badge Variants (BrioBadge)" inline>
              <BrioBadge variant="approved">Approved</BrioBadge>
              <BrioBadge variant="pending">Pending</BrioBadge>
              <BrioBadge variant="warning">Warning</BrioBadge>
              <BrioBadge variant="danger">Danger</BrioBadge>
              <BrioBadge variant="info">Info</BrioBadge>
              <BrioBadge variant="success">Success</BrioBadge>
              <BrioBadge variant="caution">Caution</BrioBadge>
              <BrioBadge variant="neutral">Neutral</BrioBadge>
            </Specimen>

            <Specimen label="Color Indicators (BrioColorIndicator)" inline>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BrioColorIndicator color="#DB6B5D" size="default" />
                  <span style={{ fontSize: 10 }}>Default (28px)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BrioColorIndicator color="#84A48C" size="compact" />
                  <span style={{ fontSize: 10 }}>Compact (20px)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BrioColorIndicator color="#9F902A" />
                  <BrioColorIndicator color="#E07A5F" />
                  <BrioColorIndicator color="#934948" />
                  <BrioColorIndicator color="#D4A72C" />
                  <span style={{ fontSize: 10 }}>Multiple</span>
                </div>
              </div>
            </Specimen>

            <Specimen label="Inline Source Badges (from SalaryCostPage)" inline>
              {[
                { label: 'Legacy', bg: '#E8EAF6', color: '#283593' },
                { label: 'Derived', bg: '#E8F5E9', color: '#2E7D32' },
                { label: 'Override', bg: '#FFF8E1', color: '#F57F17' },
                { label: 'Salary Matrix', bg: '#E3F2FD', color: '#1565C0' },
              ].map(b => (
                <span key={b.label} style={{
                  display: 'inline-block',
                  fontSize: 10,
                  padding: '2px 8px',
                  background: b.bg,
                  color: b.color,
                  fontWeight: 500,
                }}>{b.label}</span>
              ))}
            </Specimen>

            <Suggestion type="merge">
              Source badges now use semantic color variants (info, success, caution, neutral) from brioProjectColors.css palettes. No blue — info uses teal (#689581, NGO palette).
            </Suggestion>
          </Section>

          {/* ═══════ 09 — PROGRESS BARS ═══════ */}
          <Section id="progress" number="09" title="Progress Bars" description="BrioProgress auto-selects color based on value. Normal (green), Warning (gold), Danger (red).">

            <Specimen label="Auto-Color Progress (BrioProgress)">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
                {[20, 50, 75, 85, 100, 115].map(v => (
                  <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 10, fontFamily: 'monospace', minWidth: 40, textAlign: 'right' }}>{v}%</span>
                    <div style={{ flex: 1 }}>
                      <BrioProgress value={v} />
                    </div>
                  </div>
                ))}
              </div>
            </Specimen>

            <Specimen label="Tall Variant" code="tall">
              <div style={{ maxWidth: 400 }}>
                <BrioProgress value={65} tall />
              </div>
            </Specimen>

            <Specimen label="Forced Variants" code="variant">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 400 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 10, minWidth: 60 }}>normal</span>
                  <div style={{ flex: 1 }}><BrioProgress value={50} variant="normal" /></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 10, minWidth: 60 }}>warning</span>
                  <div style={{ flex: 1 }}><BrioProgress value={50} variant="warning" /></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 10, minWidth: 60 }}>danger</span>
                  <div style={{ flex: 1 }}><BrioProgress value={50} variant="danger" /></div>
                </div>
              </div>
            </Specimen>
          </Section>

          {/* ═══════ 10 — ICONS & AVATARS ═══════ */}
          <Section id="icons-avatars" number="10" title="Icons & Avatars" description="BrioUserIcon with progress ring, BrioTeamIcon for team color rings.">

            <Specimen label="User Avatars (BrioUserIcon)" inline>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <BrioUserIcon initials="PS" ringColor="#689581" fillColor="#f5f5f5" size={44} progress={75} />
                  <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>44px / 75%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <BrioUserIcon initials="JV" ringColor="#a97e53" fillColor="#f5f5f5" size={44} progress={100} />
                  <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>44px / 100%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <BrioUserIcon initials="AD" ringColor="#934948" fillColor="#f5f5f5" size={44} progress={30} />
                  <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>44px / 30%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <BrioUserIcon initials="MB" ringColor="#b2a655" fillColor="#f5f5f5" size={32} progress={50} />
                  <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>32px / 50%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <BrioUserIcon initials="X" ringColor="#666" fillColor="#f5f5f5" size={24} progress={0} />
                  <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>24px / 0%</div>
                </div>
              </div>
            </Specimen>

            <Specimen label="Team Icons (BrioTeamIcon)" inline>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {[
                  { color: '#689581', label: 'Design' },
                  { color: '#a97e53', label: 'Management' },
                  { color: '#934948', label: 'Interiors' },
                  { color: '#9F902A', label: 'Urban' },
                ].map(t => (
                  <div key={t.label} style={{ textAlign: 'center' }}>
                    <BrioTeamIcon color={t.color} size={24} ring={4} title={t.label} />
                    <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>{t.label}</div>
                  </div>
                ))}
              </div>
            </Specimen>
          </Section>

          {/* ═══════ 11 — ALERTS & HIGHLIGHTS ═══════ */}
          <Section id="alerts" number="11" title="Alerts & Highlights" description="BrioAlert for inline notices, BrioHighlightBox for prominent KPI callouts.">

            <Specimen label="Alert (BrioAlert)" code="BrioAlert">
              <BrioAlert label="Note">
                All stored values remain in base currency (INR). Display conversion is handled by useSettings().
              </BrioAlert>
            </Specimen>

            <Specimen label="Alert without label">
              <BrioAlert>
                This is an alert without a label prefix — used for general notices.
              </BrioAlert>
            </Specimen>

            <Specimen label="Info Banner (BrioInfoBanner)" code="BrioInfoBanner">
              <BrioInfoBanner>
                BrioInfoBanner is an alias for BrioAlert — same visual.
              </BrioInfoBanner>
            </Specimen>

            <Specimen label="Highlight Box (BrioHighlightBox)" code="BrioHighlightBox">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <BrioHighlightBox label="Active Markup" value="2.85×" />
                <BrioHighlightBox label="Monthly Burn Rate" value="€42,500" />
                <BrioHighlightBox label="Utilisation" value="78%" />
              </div>
            </Specimen>

            <Specimen label="KPI Block (BrioKpiBlock)" code="BrioKpiBlock">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <BrioKpiBlock label="Total Hours" value="1,240" />
                <BrioKpiBlock label="Projects Active" value="18" />
                <BrioKpiBlock label="Team Members" value="32" />
              </div>
            </Specimen>

            <Specimen label="Empty State (BrioEmptyState)" code="BrioEmptyState">
              <BrioEmptyState>
                No data available. Try adjusting your filters or date range.
              </BrioEmptyState>
            </Specimen>

            <Suggestion type="question">
              BrioHighlightBox and BrioKpiBlock serve similar purposes (displaying a label+value). Should BrioKpiBlock be deprecated in favor of BrioHighlightBox, or kept for different contexts?
            </Suggestion>
          </Section>

          {/* ═══════ 12 — MODALS ═══════ */}
          <Section id="modals" number="12" title="Modals & Dialogs" description="BrioModal with 5 width presets. Click buttons below to preview.">

            <Specimen label="Modal Triggers" inline>
              <button className="brio-btn brio-btn-secondary" onClick={() => setShowModal(true)}>
                Open Default Modal (md)
              </button>
              <button className="brio-btn brio-btn-secondary" onClick={() => setShowModalLg(true)}>
                Open Large Modal (lg)
              </button>
              <button className="brio-btn brio-btn-danger" onClick={() => setShowModalDanger(true)}>
                Open Danger Modal
              </button>
            </Specimen>

            {showModal && (
              <BrioModal
                title="Default Modal (md — 448px)"
                onClose={() => setShowModal(false)}
                footer={
                  <div className="brio-modal-footer--split">
                    <div className="brio-modal-footer__left">
                      <button className="brio-btn brio-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    </div>
                    <div className="brio-modal-footer__right">
                      <button className="brio-btn brio-btn-primary" onClick={() => setShowModal(false)}>Save Changes</button>
                    </div>
                  </div>
                }
              >
                <BrioModalBody form>
                  <BrioModalField label="Employee Name" hint="As it appears on payroll">
                    <BrioInput placeholder="Enter name..." />
                  </BrioModalField>
                  <BrioModalField label="Office">
                    <BrioSelect>
                      <option value="">Select...</option>
                      <option value="rotterdam">NL</option>
                      <option value="mumbai">IN</option>
                    </BrioSelect>
                  </BrioModalField>
                  <BrioModalDivider title="Contract Details" />
                  <BrioModalField label="Start Date">
                    <BrioDatePicker value="" onChange={() => {}} placeholder="Select start date" />
                  </BrioModalField>
                  <BrioModalField label="Notes">
                    <textarea className="brio-modal-input" rows={3} placeholder="Optional notes..." />
                  </BrioModalField>
                </BrioModalBody>
              </BrioModal>
            )}

            {showModalLg && (
              <BrioModal
                title="Large Modal (lg — 640px)"
                onClose={() => setShowModalLg(false)}
                width="lg"
                footer={
                  <div className="brio-modal-footer--split">
                    <div className="brio-modal-footer__left" />
                    <div className="brio-modal-footer__right">
                      <button className="brio-btn brio-btn-primary" onClick={() => setShowModalLg(false)}>Done</button>
                    </div>
                  </div>
                }
              >
                <BrioModalBody>
                  <p style={{ fontSize: 12, color: 'var(--brio-black)' }}>
                    This is a large modal (640px) suitable for forms with more fields,
                    side-by-side layouts, or content that needs more breathing room.
                  </p>
                  <BrioGrid cols={2} gap="s">
                    <BrioAnalyticsSurface padding="s">
                      <div style={{ fontSize: 11, color: 'var(--brio-grey)' }}>Column 1</div>
                    </BrioAnalyticsSurface>
                    <BrioAnalyticsSurface padding="s">
                      <div style={{ fontSize: 11, color: 'var(--brio-grey)' }}>Column 2</div>
                    </BrioAnalyticsSurface>
                  </BrioGrid>
                </BrioModalBody>
              </BrioModal>
            )}

            {showModalDanger && (
              <BrioModal
                title="Delete Employee Record?"
                titleDanger
                onClose={() => setShowModalDanger(false)}
                width="sm"
                footer={
                  <div className="brio-modal-footer--split">
                    <div className="brio-modal-footer__left">
                      <button className="brio-btn brio-btn-secondary" onClick={() => setShowModalDanger(false)}>Cancel</button>
                    </div>
                    <div className="brio-modal-footer__right">
                      <button className="brio-btn brio-btn-danger" onClick={() => setShowModalDanger(false)}>Delete</button>
                    </div>
                  </div>
                }
              >
                <BrioModalBody>
                  <p style={{ fontSize: 12, color: 'var(--brio-black)' }}>
                    This action cannot be undone. All associated time entries, salary history,
                    and performance reviews will be permanently deleted.
                  </p>
                </BrioModalBody>
              </BrioModal>
            )}
          </Section>

          {/* ═══════ 13 — PAGE HEADERS & NAVIGATION ═══════ */}
          <Section id="headers" number="13" title="Page Headers & Navigation" description="BrioPageHeader for titles, BrioPageSubHeader for tab navigation, BrioSectionHeader for in-page sections.">

            <Specimen label="Page Header (BrioPageHeader)" code="BrioPageHeader">
              <div style={{ border: '1px solid var(--brio-border)', marginBottom: 16 }}>
                <BrioPageHeader
                  title="Project Profitability"
                  subtitle="Financial overview for all active projects"
                  right={
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="brio-btn brio-btn-secondary"><Download size={12} /> Export</button>
                      <button className="brio-btn brio-btn-primary"><Plus size={12} /> New Project</button>
                    </div>
                  }
                />
              </div>
            </Specimen>

            <Specimen label="Page Sub-Header Tabs (BrioPageSubHeader)" code="BrioPageSubHeader">
              <div style={{ border: '1px solid var(--brio-border)' }}>
                <BrioPageSubHeader
                  items={[
                    { key: 'overview', label: 'Overview' },
                    { key: 'projects', label: 'Projects', icon: <Briefcase size={12} /> },
                    { key: 'team', label: 'Team', icon: <Users size={12} /> },
                    { key: 'settings', label: 'Settings', icon: <Settings size={12} /> },
                  ]}
                  activeKey={activeTab}
                  onSelect={setActiveTab}
                  right={
                    <BrioIconButton icon={<Filter size={14} />} title="Filter" />
                  }
                />
              </div>
            </Specimen>

            <Specimen label="Section Header (BrioSectionHeader)" code="BrioSectionHeader">
              <BrioSectionHeader
                title="Monthly Breakdown"
                right={
                  <button className="brio-btn brio-btn-secondary" style={{ fontSize: 10 }}>
                    <RefreshCw size={11} /> Refresh
                  </button>
                }
              />
            </Specimen>

            <Specimen label="Frame Header (BrioFrameHeader)" code="BrioFrameHeader">
              <BrioFrameHeader
                title="Active Projects"
                right={<span style={{ fontSize: 10, color: 'var(--brio-grey)' }}>18 total</span>}
              />
            </Specimen>

            <Specimen label="Pageband Variants (CSS classes)" code=".brio-pageband">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="brio-pageband brio-pageband--header" style={{ padding: '12px 24px' }}>
                  <span style={{ fontSize: 11 }}>.brio-pageband--header (white bg, border bottom)</span>
                </div>
                <div className="brio-pageband brio-pageband--filter" style={{ padding: '12px 24px' }}>
                  <span style={{ fontSize: 11 }}>.brio-pageband--filter (grey-light bg)</span>
                </div>
                <div className="brio-pageband brio-pageband--local" style={{ padding: '12px 24px' }}>
                  <span style={{ fontSize: 11 }}>.brio-pageband--local (grey-light bg, for local nav)</span>
                </div>
              </div>
            </Specimen>
          </Section>

          {/* ═══════ 14 — SURFACES & CARDS ═══════ */}
          <Section id="surfaces" number="14" title="Surfaces & Cards" description="Container components for grouping content. BrioAnalyticsSurface is the primary surface.">

            <Specimen label="Analytics Surface Padding Variants" code="BrioAnalyticsSurface">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {['xs', 's', 'm', 'l'].map(p => (
                  <BrioAnalyticsSurface key={p} padding={p}>
                    <span style={{ fontSize: 11, color: 'var(--brio-grey)' }}>
                      padding="{p}" ({p === 'xs' ? '8px' : p === 's' ? '16px' : p === 'm' ? '24px' : '32px'})
                    </span>
                  </BrioAnalyticsSurface>
                ))}
              </div>
            </Specimen>

            <Specimen label="BrioCard (Legacy Alias)" code="BrioCard">
              <BrioCard>
                <span style={{ fontSize: 11, color: 'var(--brio-grey)' }}>
                  BrioCard is an alias for BrioAnalyticsSurface — same visual
                </span>
              </BrioCard>
            </Specimen>

            <Specimen label="BrioGrid (2, 3, 4 columns)" code="BrioGrid">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <Label>cols=2</Label>
                  <BrioGrid cols={2} gap="s">
                    <BrioAnalyticsSurface padding="s"><span style={{ fontSize: 10 }}>Cell 1</span></BrioAnalyticsSurface>
                    <BrioAnalyticsSurface padding="s"><span style={{ fontSize: 10 }}>Cell 2</span></BrioAnalyticsSurface>
                  </BrioGrid>
                </div>
                <div>
                  <Label>cols=3</Label>
                  <BrioGrid cols={3} gap="s">
                    <BrioAnalyticsSurface padding="s"><span style={{ fontSize: 10 }}>Cell 1</span></BrioAnalyticsSurface>
                    <BrioAnalyticsSurface padding="s"><span style={{ fontSize: 10 }}>Cell 2</span></BrioAnalyticsSurface>
                    <BrioAnalyticsSurface padding="s"><span style={{ fontSize: 10 }}>Cell 3</span></BrioAnalyticsSurface>
                  </BrioGrid>
                </div>
              </div>
            </Specimen>

            <Specimen label="BrioSection with Title" code="BrioSection">
              <BrioSection title="Revenue Breakdown">
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', padding: '8px 0' }}>
                  Content goes here — the title uses .brio-analytics-section-title
                </div>
              </BrioSection>
            </Specimen>
          </Section>

          {/* ═══════ 15 — PROJECT TYPE COLORS ═══════ */}
          <Section id="project-colors" number="15" title="Project Type Colors" description="Each project category has a dedicated color palette defined in brioProjectColors.css.">
            {[
              { type: 'Cultural', colors: ['#DB6B5D', '#E28069', '#A66257', '#D3967A', '#994b41'] },
              { type: 'Educational', colors: ['#F1B476', '#5f7d6e', '#779c8a', '#92b0a1', '#adc4b9'] },
              { type: 'Houses', colors: ['#ECD288', '#a5935f', '#bda86d', '#f0dba0', '#f4e4b8'] },
              { type: 'Housing', colors: ['#c59b76', '#f6c293', '#f8cea9', '#fadabe', '#fbe7d4'] },
              { type: 'Hospitality', colors: ['#c0886e', '#f0aa89', '#f3bba1', '#f6ccb8', '#f9ddd0'] },
              { type: 'Institutional', colors: ['#854e46', '#a66257', '#b88179', '#caa19a', '#dbc0bc'] },
              { type: 'Interior', colors: ['#c39868', '#f4be82', '#f6cb9b', '#f8d8b4', '#fbe5cd'] },
              { type: 'NGO', colors: ['#537767', '#689581', '#86aa9a', '#a4bfb3', '#c3d5cd'] },
              { type: 'Public', colors: ['#969e78', '#bcc596', '#c9d1ab', '#d7dcc0', '#e4e8d5'] },
              { type: 'BRIO (Internal)', colors: ['#714035', '#5a332a', '#53312c', '#7a5f41', '#77523b'] },
            ].map(cat => (
              <div key={cat.type} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>{cat.type}</div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {cat.colors.map((c, i) => (
                    <div key={i} style={{ width: 48, height: 24, background: c, position: 'relative' }} title={c}>
                      <span style={{
                        position: 'absolute',
                        bottom: 1,
                        left: 2,
                        fontSize: 7,
                        color: i < 2 ? '#fff' : '#333',
                        fontFamily: 'monospace',
                      }}>{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <Suggestion type="keep">
              Project color palettes are well-crafted with good progression from dark to light. Each category is distinct.
            </Suggestion>
          </Section>

          {/* ═══════ 16 — ROLE SENIORITY COLORS ═══════ */}
          <Section id="role-colors" number="16" title="Role Seniority Colors" description="CSS variables for job role hierarchy, used in user icons and team displays.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { role: 'Director', var: '--brio-role-director', color: '#a97e53' },
                { role: 'Partner', var: '--brio-role-partner', color: '#a97e53' },
                { role: 'Associate', var: '--brio-role-associate', color: '#49685a' },
                { role: 'Senior Architect', var: '--brio-role-senior-architect', color: '#689581' },
                { role: 'Project Architect', var: '--brio-role-project-architect', color: '#7f7322' },
                { role: 'Medior Architect', var: '--brio-role-medior-architect', color: '#b2a655' },
                { role: 'Junior Architect', var: '--brio-role-junior-architect', color: '#d9d3aa' },
                { role: 'Intern', var: '--brio-role-intern', color: '#f0b596' },
                { role: 'Support', var: '--brio-role-support', color: '#d3b1ab' },
              ].map(r => (
                <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 20, background: r.color, flexShrink: 0 }} />
                  <BrioUserIcon initials={r.role.split(' ').map(w => w[0]).join('')} ringColor={r.color} fillColor="#f5f5f5" size={28} progress={80} />
                  <span style={{ fontSize: 11, fontWeight: 500, minWidth: 140 }}>{r.role}</span>
                  <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>
                    {r.var} · {r.color}
                  </span>
                </div>
              ))}
            </div>

            <Suggestion type="question">
              Director and Partner share the same color (#a97e53). Is this intentional or should they be differentiated?
            </Suggestion>
          </Section>

          {/* ═══════ 17 — ICON LIBRARY ═══════ */}
          <Section id="icons-library" number="17" title="Icon Library (lucide-react)" description="All icons used across the app, grouped by function. Sizes: 12px (small), 14px (default), 16px (large).">

            {[
              {
                group: 'Navigation',
                icons: [
                  { Icon: ChevronDown, name: 'ChevronDown' },
                  { Icon: ChevronUp, name: 'ChevronUp' },
                  { Icon: ChevronRight, name: 'ChevronRight' },
                  { Icon: ExternalLink, name: 'ExternalLink' },
                ],
              },
              {
                group: 'Actions',
                icons: [
                  { Icon: Plus, name: 'Plus' },
                  { Icon: Edit2, name: 'Edit2' },
                  { Icon: Trash2, name: 'Trash2' },
                  { Icon: Copy, name: 'Copy' },
                  { Icon: Download, name: 'Download' },
                  { Icon: Upload, name: 'Upload' },
                  { Icon: RefreshCw, name: 'RefreshCw' },
                  { Icon: Search, name: 'Search' },
                  { Icon: Filter, name: 'Filter' },
                  { Icon: MoreHorizontal, name: 'MoreHorizontal' },
                ],
              },
              {
                group: 'Status',
                icons: [
                  { Icon: Check, name: 'Check' },
                  { Icon: X, name: 'X' },
                  { Icon: AlertTriangle, name: 'AlertTriangle' },
                  { Icon: AlertCircle, name: 'AlertCircle' },
                  { Icon: Loader2, name: 'Loader2' },
                  { Icon: Eye, name: 'Eye' },
                  { Icon: EyeOff, name: 'EyeOff' },
                ],
              },
              {
                group: 'Domain',
                icons: [
                  { Icon: Clock, name: 'Clock' },
                  { Icon: Users, name: 'Users' },
                  { Icon: Briefcase, name: 'Briefcase' },
                  { Icon: MapPin, name: 'MapPin' },
                  { Icon: Mail, name: 'Mail' },
                  { Icon: Paperclip, name: 'Paperclip' },
                  { Icon: Calendar, name: 'Calendar' },
                  { Icon: FileText, name: 'FileText' },
                  { Icon: Settings, name: 'Settings' },
                  { Icon: Star, name: 'Star' },
                  { Icon: Heart, name: 'Heart' },
                ],
              },
            ].map(group => (
              <Specimen key={group.group} label={group.group} inline>
                {group.icons.map(({ Icon, name }) => (
                  <div key={name} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    padding: '8px 6px',
                    minWidth: 52,
                  }}>
                    <Icon size={16} style={{ color: 'var(--brio-black)' }} />
                    <span style={{ fontSize: 8, fontFamily: 'monospace', color: 'var(--brio-grey)' }}>{name}</span>
                  </div>
                ))}
              </Specimen>
            ))}

            <Specimen label="Icon Sizes (12 / 14 / 16)" inline>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Edit2 size={12} /> <span style={{ fontSize: 9 }}>12px (small)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Edit2 size={14} /> <span style={{ fontSize: 9 }}>14px (default)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Edit2 size={16} /> <span style={{ fontSize: 9 }}>16px (large)</span>
                </div>
              </div>
            </Specimen>
          </Section>

          {/* ═══════ 18 — DESIGN SUGGESTIONS ═══════ */}
          <Section id="suggestions" number="18" title="Design Suggestions" description="My recommendations as a UI/graphic design expert, based on the full audit.">

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>A. Typography Token Gap</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  The token scale goes up to 13px, but page headers use 24px and modal titles use 15px.
                  These are "orphan sizes" not represented in the design token system.
                </div>
                <Suggestion type="merge">
                  Resolved: Added --brio-font-size-xl (16px) and --brio-font-size-2xl (24px). 15px removed from type scale.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>B. Dual Input Styles</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  Two input patterns exist: .brio-modal-input (from BrioComponents) and an inline INPUT_STYLE object
                  (from ManageProjectsPage/TeamMembersPage). They differ in border-radius (4px vs 6px) and padding.
                </div>
                <Suggestion type="replace">
                  Consolidate to one input style. The .brio-modal-input (4px radius) aligns better with the flat BRIO aesthetic.
                  The inline INPUT_STYLE should be removed and those pages should use BrioInput.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>C. Hardcoded Colors</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  Several pages use hex colors not in the BRIO token system: #d97706 (amber), #16a34a (green),
                  #3B82F6 (blue), #dc2626 (red), #1e293b (slate). These come from Tailwind defaults, not BRIO.
                </div>
                <Suggestion type="replace">
                  Map all Tailwind-inherited colors to BRIO tokens. If a Blue semantic color is needed (for "info" states),
                  add --brio-blue officially rather than using hardcoded Tailwind values.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>D. Badge Inconsistency</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  BrioBadge has 4 variants (approved, pending, warning, danger), but SalaryCostPage creates
                  custom source badges with entirely different colors (#E8EAF6 blue, #E8F5E9 green, etc.).
                  HiringPage and CalendarPage also have custom badge colors.
                </div>
                <Suggestion type="merge">
                  Add BrioBadge variants for "info" (teal, from NGO palette — no blue) and "neutral" (grey). Convert all custom badge-like
                  spans to use BrioBadge for consistency.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>E. Border Radius Strategy</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  The BRIO system is deliberately flat (0px border-radius on cards, tables, surfaces), with 8px only on modals
                  and 4px on inputs. But several pages use 5px, 6px, 8px, 12px border-radius in inline styles,
                  creating visual inconsistency.
                </div>
                <Suggestion type="replace">
                  Define border-radius tokens: --brio-radius-none (0), --brio-radius-sm (4px), --brio-radius-md (8px).
                  Enforce: surfaces/cards = none, inputs = sm, modals = md. Remove all other values.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>F. Inline Style Proliferation</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  The audit found ~4,230 inline style instances across 34 files. While some are necessary for dynamic values
                  (conditional colors, computed widths), many are repeated static patterns (fontSize: 11px, fontWeight: 600,
                  gap: 6px) that could be BRIO utility classes.
                </div>
                <Suggestion type="merge">
                  Add utility classes for the most common inline patterns: .brio-text-11 (11px), .brio-fw-600 (600 weight),
                  .brio-gap-6 (6px). This would reduce inline styles by ~40% and improve consistency.
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>G. Component Aliasing</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  Several components are aliases: BrioCard = BrioAnalyticsSurface, BrioInfoBanner = BrioAlert,
                  BrioProgressBar = BrioProgress, BrioSurface = BrioAnalyticsSurface. This creates confusion about
                  which component to use.
                </div>
                <Suggestion type="question">
                  Should we deprecate the aliases and standardize on one name per component? Or keep them for
                  semantic clarity (e.g., BrioCard for generic containers, BrioAnalyticsSurface for analytics)?
                </Suggestion>
              </BrioAnalyticsSurface>

              <BrioAnalyticsSurface padding="m">
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>H. Missing Interactive States</div>
                <div style={{ fontSize: 11, color: 'var(--brio-grey)', lineHeight: 1.6 }}>
                  Focus states are limited to inputs (gold border on focus). Buttons, tabs, and icon buttons
                  lack visible focus indicators, which affects keyboard navigation accessibility.
                </div>
                <Suggestion type="merge">
                  Add a subtle gold outline/ring for focus-visible on all interactive elements:
                  buttons, tabs, icon buttons. Use outline: 2px solid var(--brio-gold-light).
                </Suggestion>
              </BrioAnalyticsSurface>

            </div>
          </Section>

          {/* ── Footer ─────────────────────────────────────────────── */}
          <div style={{
            borderTop: '1px solid var(--brio-border)',
            paddingTop: 24,
            marginTop: 48,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: 'var(--brio-grey)', fontStyle: 'italic' }}>
              BRIO Design System Style Guide — Temporary Page for Design Review
            </div>
            <div style={{ fontSize: 9, color: 'var(--brio-grey)', marginTop: 4 }}>
              Use section numbers (01–18) and suggestion labels (A–H) when providing feedback.
              Example: "In 06, I want table code 1a but with striped rows from 5a" or "Do suggestion E (border radius tokens)"
            </div>
          </div>

        </BrioFrame>
      </div>
    </div>
  );
}
